// AI Generated Test
import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { PagePdfAccessibilityService } from '@client/app/services/entities/pdf-accessibility/page-pdf-accessibility.service';
import { PdfService } from '@client/app/services/entities/pdf/pdf.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { sortByKeys } from '@client/app/shared/utils/object-utils';

@Component({
    selector: 'document-details-section-accessibility',
    standalone: false,
    template: `
        <div class="document-details-section-accessibility d-flex flex-column full-height">
            <div>
                <page-header [icon]="['MENUES', 'ACCESSIBILITY'] | monIcons"
                             [header]="headline">
                    <page-header-content>
                        <div class="row">
                            <div class="col-48 text-right" *ngIf="hasPdfAccessibilityQueuedAt()">
                                <csv-export-button
                                    [exports]="exports"
                                    clsMargin="ml-1"
                                    clsSize="px-3"
                                ></csv-export-button>
                                <button *ngIf="document.clarity_queued_at === null"
                                        [disabled]="'pdf_accessibility' | hasDemoAccess"
                                        (click)="requestClarification()"
                                        class="btn btn-border-radius btn-secondary"
                                        type="button"
                                        translate>
                                    Start PDF accessibility scan
                                </button>
                            </div>
                        </div>
                    </page-header-content>
                </page-header>
            </div>
            <div class="alert alert-info mb-0" *ngIf="document.clarity_queued_at !== null" translate>
                PDF accessibility scan requested
            </div>
            <div class="alert alert-info mb-0"
                 *ngIf="document.clarity_queued_at === null && document.clarity_checked_at !== null"
                 [translate-args]="{date: document.clarity_checked_at | userDate}"
                 translate>
                PDF accessibility scan completed at [[date]]
            </div>
            <div class="alert alert-danger not-tagged mb-0" *ngIf="document.clarity_tagged === false">
                <span [ngClass]="['ACCESSIBILITY', 'DOCUMENTDETAILS', 'NOT_TAGGED'] | monIcons" class="triangle-icon mr-1"></span>
                 <span class="text-bold" translate>The Document is not tagged</span>
            </div>
            <div class="alert alert-info mb-0" *ngIf="document.clarity_checked_at === null">
                <span [ngClass]="['ACCESSIBILITY', 'DOCUMENTDETAILS', 'NOT_SCANNED'] | monIcons" class="not-scanned-icon mr-1"></span>
                <span class="text-bold not-scanned-text" translate>Please perform a PDF scan to see the results for the checks below</span>
            </div>
            <div class="flex-grow-1 d-flex align-items-stretch row">
                <div class="col-48 col-md-30 pr-md-0">
                    <mon-spinner [loading]="loading">
                        <document-details-section-accessibility-wcag20-table [activeCheck]="check"
                                                                             [document]="document"
                                                                             (selectCheck)="selectCheck($event)"
                                                                             [checks]="checks"
                                                                             [progress]="progress">
                        </document-details-section-accessibility-wcag20-table>
                    </mon-spinner>
                </div>
                <div class="col-48 col-md-18 pl-md-0 inverse-shadow bg-light-grey scroll">
                    <document-details-section-accessibility-information [check]="check"
                                                                        *ngIf="hasErrors(check)"
                                                                        [document]="document"
                                                                        [errors]="checkErrors"
                                                                        (onTabChange)="onTabChange($event)"
                                                                        [loading]="loadingErrors"
                                                                        [tab]="tab">
                    </document-details-section-accessibility-information>
                </div>
            </div>
        </div>
    `,
})
export class DocumentDetailsSectionAccessibilityComponent implements OnInit {
    @Input() document: any;
    @Input() page: any;
    @Output() callback = new EventEmitter<any>();
    @Output() updateDocument = new EventEmitter<any>();

    exports: any[] = [];
    domain: any;
    checks: any[] = [];
    check: any = null;
    checkErrors: any[] = [];
    tab: any = null;
    progress: any;
    loading = false;
    loadingErrors = false;
    headline = '';

    constructor (
        private pagePdfAccessibilityService: PagePdfAccessibilityService,
        private pdfService: PdfService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private sessionService: SessionService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    activate (): void {
        this.exports = [
            {
                name: this.translateService.getString('Start export PDF Accessibility Report'),
                data: {
                    category: 'page_clarity',
                    params: {
                        page_id: this.page.id,
                    },
                },
            },
        ];
        this.domain = this.sessionService.domain;
        this.checks = [];
        this.check = null;
        this.checkErrors = [];
        this.tab = null;
        this.getPage();
        this.headline = this.translateService.getString('Accessibility Compliance Scan');
    }

    getPage (): void {
        this.progress = this.pagePdfAccessibilityService.getAllChecks(this.document.id, undefined).then((checks: any[]) => {
            this.checks = sortByKeys(checks, ['errors_count']).reverse();
            this.selectCheck(this.check);
        }, () => {});
    }

    getErrors (): void {
        if (this.check === null || isNaN(parseInt(this.check.id))) {
            this.checkErrors = [];
            return;
        }
        this.loadingErrors = true;
        const params = {
            page_size: 0,
            result_type: this.tab,
        };
        this.pagePdfAccessibilityService.getCheckErrors(this.document.id, this.check.id, params)
            .then((errors: any[]) => {
                this.checkErrors = errors;
            }, () => {})
            .finally(() => {
                this.loadingErrors = false;
            });
    }

    onTabChange (tab: any): void {
        this.tab = tab;
        this.getErrors();
    }

    selectCheck (check: any): void {
        if (check === null) {
            this.check = this.checks[0] || null;
        } else {
            this.check = this.checks.find(item => item.id === check.id) || null;
        }
        this.getErrors();
    }

    hasErrors (check: any): boolean {
        if (check) {
            return check.errors_count > 0;
        } else {
            return false;
        }
    }

    requestClarification (): void {
        const translateParams = {
            name: '<strong>' + this.document.title || this.document.url + '</strong>',
        };
        const translation = this.translateService.getString(
            'Are you sure you want to the request ADA clarification for [[name]]?',
            translateParams,
        );
        this.monModalService.confirm(translation).then(() => {
            this.pdfService.createScan(this.document).then((document: any) => {
                this.updateDocument.emit(document);
                this.getPage();
            }, () => {});
        }, () => {});
    }

    hasPdfAccessibilityQueuedAt (): boolean {
        return this.document.hasOwnProperty('clarity_queued_at');
    }
}
